import { useTracking } from "@/requests/hooks/app";
import { faCopy } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";

const styles = tv({
  slots: {
    container: "relative max-w-full rounded bg-gray-light px-20 pb-20 pt-40",
    copyContainer:
      "absolute left-0 top-0 flex min-w-[200px] cursor-pointer flex-row items-center justify-center gap-10 rounded-tl bg-gradient-to-r from-primary to-green px-20 py-5 !text-caption font-semibold uppercase text-white",
    codeContainer:
      "max-h-150 overflow-x-hidden whitespace-pre-wrap text-caption",
  },
  variants: {
    isCode: {
      true: { codeContainer: "break-all font-mono" },
      false: { codeContainer: "sms-onboarding-policy" },
    },
  },
});

interface IProps {
  content?: string;
  trackingId?: string;
  isCode?: boolean;
  ref?: MutableRefObject<HTMLDivElement | null>;
  copyRichText?: boolean;
}

const CopyContent = forwardRef(
  (
    { content, trackingId, isCode = false, copyRichText = true }: IProps,
    ref: ForwardedRef<HTMLDivElement | null>,
  ) => {
    const { container, copyContainer, codeContainer } = styles({ isCode });

    const { t } = useTranslation();
    const { mutate: sendTracking } = useTracking();

    const [copyText, setCopyText] = useState<string>(
      t("components.copyContent.copy"),
    );

    useEffect(() => {
      if (copyText === t("components.copyContent.copied"))
        setTimeout(() => {
          setCopyText(t("components.copyContent.copy"));
        }, 1000);
    }, [copyText]);

    const copyRich = async (rich: string) => {
      if (typeof ClipboardItem !== "undefined") {
        const html = new Blob([rich], { type: "text/html" });
        const text = new Blob([rich], { type: "text/plain" });
        const data = new ClipboardItem({
          "text/html": html,
          "text/plain": text,
        });
        await navigator.clipboard.write([data]);
      } else {
        const cb = (e: ClipboardEvent) => {
          e.clipboardData?.setData("text/html", rich);
          e.clipboardData?.setData("text/plain", rich);
          e.preventDefault();
        };
        document.addEventListener("copy", cb);
        document.execCommand("copy");
        document.removeEventListener("copy", cb);
      }
    };

    const handleCopyCode = () => {
      if (!content) return;

      if (copyRichText) copyRich(content);
      else navigator.clipboard.writeText(content);

      setCopyText(t("components.copyContent.copied"));

      if (trackingId)
        sendTracking({
          eventName: trackingId,
        });
    };

    return (
      <div className={container()} data-testid="copy-code">
        <div className={copyContainer()} onClick={handleCopyCode}>
          <FontAwesomeIcon icon={faCopy} />
          {copyText}
        </div>
        <div className={codeContainer()} ref={ref}>
          {content}
        </div>
      </div>
    );
  },
);

export default CopyContent;
